import api from 'services/api';
import { REJECT, array, nullable, num, object, str, type FieldParser, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { retentionPolicySchema, type RetentionPolicy } from 'api/shared/retentionPolicy';

/**
 * The FE-authored request body for creating/updating a zone. **Permissive on
 * purpose** — the server owns the rules (name required, retention shape) and
 * returns 400. One shape for both writes: `postZone` sends it as-is; `putZone`
 * maps `name` → the wire's `new_name` (the URL carries the original name), so
 * callers think in terms of "the zone's name" either way.
 */
export interface SaveZone {
  readonly name: string | null;
  readonly description: string | null;
  readonly retention_policies: readonly RetentionPolicy[];
}

// The full zone record returned by both POST and PUT — the backend serializes
// both through the SAME `getZoneDetails(client, name)` helper (routes/zones.js),
// so this is shared by IDENTITY (one query, one shape). Declared here (the
// create endpoint); `putZone` imports it + `zoneSchema`.
export interface Zone {
  readonly zone_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly retention_policies: readonly RetentionPolicy[];
}

export const zoneSchema: FieldParser<Zone> = object<Zone>({
  zone_id: num(REJECT),
  name: str(),
  description: nullable(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  retention_policies: array(retentionPolicySchema),
});

/**
 * POST `/zones`. Returns the created zone.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const postZone = async (save: SaveZone): Promise<Received<Zone>> => {
  const res = await api.post('/zones', {
    name: save.name,
    description: save.description,
    retention_policies: save.retention_policies,
  });
  return readResponse(res.data, { schema: zoneSchema, source: 'postZone' });
};
